<script setup>
import { ref, computed, useSlots } from 'vue';

defineProps({
    titulo: {
        type: [String, Number],
    },
    descricao: {
        type: String,
    },
    labelExpandir: {
        type: String,
        default: 'Ver mais',
    },
    labelRecolher: {
        type: String,
        default: 'Ver menos',
    },
});

const slots = useSlots();

const expandido = ref(false);

const temDetalhes = computed(() => Boolean(slots.detalhes));

const alternarDetalhes = () => {
    expandido.value = !expandido.value;
};
</script>

<template>
    <div class="flex flex-column gap-2 w-full">
        <!-- Faixa superior: status à esquerda, informação de apoio à direita (data, valor, etc.) -->
        <div v-if="slots.topo" class="flex align-items-center justify-content-between gap-2 flex-wrap">
            <slot name="topo"></slot>
        </div>

        <div class="flex align-items-start justify-content-between gap-2">
            <div class="flex flex-column flex-1 min-w-0" style="gap: 0.19rem;">
                <h4 v-if="titulo" class="font-semibold text-sm m-0">{{ titulo }}</h4>

                <slot name="titulo"></slot>

                <p v-if="descricao" class="font-medium text-xs text-500 m-0">
                    {{ descricao }}
                </p>

                <div v-if="slots.descricao" class="font-medium text-xs text-500">
                    <slot name="descricao"></slot>
                </div>
            </div>
        </div>

        <!-- Conteúdo sempre visível abaixo do bloco principal (tags, progresso, etc.) -->
        <div v-if="slots.conteudo" class="flex flex-row flex-wrap align-items-center gap-2">
            <slot name="conteudo"></slot>
        </div>

        <!-- Demais informações, reveladas sob demanda para manter o card curto -->
        <div v-if="temDetalhes" class="area-expansivel" :class="{ 'area-expansivel-aberta': expandido }">
            <div class="overflow-hidden">
                <div class="conteudo-expansivel flex flex-column gap-1 py-3 mt-1 border-top-1 surface-border text-xs text-gray-500 font-normal">
                    <slot name="detalhes"></slot>
                </div>
            </div>
        </div>

        <!-- Rodapé: "Ver mais" à esquerda (quando há detalhes) e ações sempre à direita -->
        <div v-if="temDetalhes || slots.acoes" class="flex align-items-center justify-content-between">
            <button v-if="temDetalhes" type="button"
                class="botao-expandir flex align-items-center gap-1 p-0 text-xs font-normal text-400"
                :aria-expanded="expandido" @click.stop="alternarDetalhes">
                <span class="line-height-1">{{ expandido ? labelRecolher : labelExpandir }}</span>
                <i class="pi pi-chevron-down icone-expandir"
                    :class="{ 'icone-expandir-aberto': expandido }"></i>
            </button>

            <div v-if="slots.acoes" class="flex-shrink-0 ml-auto">
                <slot name="acoes"></slot>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Transição de altura sem medir o conteúdo: grid de 0fr para 1fr.
   A curva desacelera no fim para o card "assentar" em vez de parar seco. */
.area-expansivel {
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 0.32s cubic-bezier(0.32, 0.72, 0, 1);
}

.area-expansivel-aberta {
    grid-template-rows: 1fr;
}

/* O conteúdo acompanha a abertura com fade e um leve deslize, sem antecipar o espaço */
.conteudo-expansivel {
    opacity: 0;
    transform: translateY(-0.25rem);
    transition: opacity 0.2s ease, transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
}

.area-expansivel-aberta .conteudo-expansivel {
    opacity: 1;
    transform: translateY(0);
    /* Só aparece depois que a altura já começou a abrir */
    transition-delay: 0.08s;
}

.botao-expandir {
    width: max-content;
    border: none;
    background: none;
    cursor: pointer;
    color: var(--primary-color, #3b82f6);
    /* Sem o realce de toque do mobile nem o contorno do clique de mouse */
    -webkit-tap-highlight-color: transparent;
}

.botao-expandir:focus {
    outline: none;
}

/* O contorno volta na navegação por teclado */
.botao-expandir:focus-visible {
    outline: 2px solid var(--primary-color, #3b82f6);
    outline-offset: 2px;
    border-radius: 3px;
}

/* Box quadrado com o glyph centralizado: o ícone deixa de herdar o line-height do texto
   e a rotação passa a girar em torno do próprio centro */
.icone-expandir {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    font-size: 0.65rem;
    line-height: 1;
    transform-origin: center;
    transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
}

.icone-expandir-aberto {
    transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {

    .area-expansivel,
    .conteudo-expansivel,
    .icone-expandir {
        transition: none;
    }

    .conteudo-expansivel {
        opacity: 1;
        transform: none;
    }
}
</style>
